import {ColumnSpec, ColumnState, GridSorter, GridSorterLike} from '@xh/hoist/cmp/grid';
import {FieldSpec, FieldType, genDisplayName} from '@xh/hoist/data';
import {castArray, isEmpty} from 'lodash';
import {
    allPositionFields,
    avgPriceCol,
    betaCol,
    brokerageCol,
    intradayCol,
    lastDividendCol,
    marketValueCol,
    pnlDlyCol,
    pnlOpenCol,
    pnlDlyPctCol,
    pnlYtdCol,
    portfolioManagementTypeCol,
    portfolioNameCol,
    portfolioTaxTypeCol,
    priceChangeCol,
    priceChangePctCol,
    priceCol,
    quantityCol,
    sectorCol,
    tickerChangeClassRules,
    tickerCol,
    totalCostCol,
    weightCol,
    yearRangeCol
} from '../../../../cmp/PositionColumns';
import {
    DEFAULT_GRID_SPEC,
    FieldCatalog,
    FieldCatalogEntry,
    FieldDataType,
    FormatSpec,
    GridSpec,
    GridSpecCapabilities,
    GridSpecColumn,
    GridSpecFilter,
    GridSpecTotals
} from '../../../../cmp/gridSpec/GridSpecTypes';

/** Fields that are internal ids - not offered in the grid. */
const HIDDEN_FIELDS = ['portfolioId', 'securityId'];

const BASE_COLUMNS: Record<string, ColumnSpec> = {
    // Ticker cell filled by today's move under the terminal style, as in the Portfolio tab.
    ticker: {...tickerCol, cellClassRules: tickerChangeClassRules},
    brokerage: brokerageCol,
    portfolioName: portfolioNameCol,
    portfolioManagementType: portfolioManagementTypeCol,
    portfolioTaxType: portfolioTaxTypeCol,
    price: priceCol,
    priceChange: priceChangeCol,
    priceChangePct: priceChangePctCol,
    quantity: quantityCol,
    marketValue: marketValueCol,
    avgPrice: avgPriceCol,
    totalCost: totalCostCol,
    pnlDly: pnlDlyCol,
    pnlYtd: pnlYtdCol,
    pnlOpen: pnlOpenCol,
    beta: betaCol,
    lastDividend: lastDividendCol,
    sector: sectorCol,
    intraday: intradayCol,
    yearRange: yearRangeCol,
    weight: weightCol,
    pnlDlyPct: pnlDlyPctCol
};

const UNITS: Record<string, string> = {
    price: 'USD',
    priceChange: 'USD',
    priceChangePct: '%',
    marketValue: 'USD',
    avgPrice: 'USD',
    totalCost: 'USD',
    pnlDly: 'USD',
    pnlYtd: 'USD',
    pnlOpen: 'USD',
    lastDividend: 'USD',
    yearLow: 'USD',
    yearHigh: 'USD'
};

export const positionsBaseColumn = (field: string): ColumnSpec => BASE_COLUMNS[field] ?? {field};

/**
 * Chart columns - drawn rather than printed, from today's series and the 1Y range. Display only,
 * so not for grouping, filtering or totals.
 */
const CHART_COLUMNS: FieldCatalogEntry[] = [
    {name: 'intraday', label: 'Today', type: 'string', description: "Sparkline of today's price path, or a portfolio row's balance path. Display only."},
    {name: 'yearRange', label: '1Y Range', type: 'string', description: '52-week low-high range with the current price marked. Display only.'},
    {name: 'weight', label: 'Weight', type: 'string', description: "Bar of the row's share of the gross market value of all rows. Display only."},
    {name: 'pnlDlyPct', label: 'P&L %', type: 'string', description: "Today's P&L as a share of the market value at the prior close. Display only."}
];

export const positionsCatalog: FieldCatalog = [
    ...allPositionFields
        .filter(f => !HIDDEN_FIELDS.includes(f.name))
        .map(f => ({
            name: f.name,
            label: (BASE_COLUMNS[f.name]?.headerName as string) ?? genDisplayName(f.name),
            type: toDataType(f.type),
            unit: UNITS[f.name],
            isDimension: !!f.isDimension
        })),
    ...CHART_COLUMNS
];

/** Store fields for the grid - every position field, so any of them can be added as a column. */
export const positionsStoreFields: FieldSpec[] = allPositionFields.map(({name, type}) => ({name, type}));

export const positionsCapabilities: GridSpecCapabilities = {
    grouping: 'external',
    filtering: 'external',
    totals: true,
    leafRows: true
};

export const positionsDefaultSpec: GridSpec = {
    ...DEFAULT_GRID_SPEC,
    columns: [
        {field: 'ticker'},
        {field: 'brokerage'},
        {field: 'portfolioName'},
        {field: 'price'},
        {field: 'quantity'},
        {field: 'marketValue', aggregator: 'sum'},
        {field: 'pnlDly', aggregator: 'sum'},
        {field: 'pnlYtd', aggregator: 'sum'}
    ],
    groupBy: ['ticker'],
    stripeRows: false
};

function toDataType(type: FieldType | string): FieldDataType {
    switch (type) {
        case FieldType.NUMBER:
        case FieldType.INT:
            return 'number';
        case FieldType.DATE:
        case FieldType.LOCAL_DATE:
            return 'date';
        case FieldType.BOOL:
            return 'bool';
        default:
            return 'string';
    }
}

//------------------------
// Migration of the pre-GridSpec positions state
//------------------------
interface LegacyColumnFormat {
    type: 'text' | 'number' | 'date';
    align: 'left' | 'center' | 'right';
    headerName?: string;
    flex?: boolean;
    numberStyle?: 'plain' | 'millions' | 'percent';
    precision?: number;
    currency?: boolean;
    colorSpec?: boolean;
    ledger?: boolean;
    dateStyle?: 'date' | 'dateTime' | 'dateTimeSec';
}

export interface LegacyPositionsState {
    filters?: GridSpecFilter[];
    columns?: ColumnState[];
    sortBy?: GridSorterLike | GridSorterLike[];
    groupBy?: string[];
    columnFormats?: Record<string, LegacyColumnFormat>;
    showLeafRows?: boolean;
    totals?: GridSpecTotals | boolean;
    rowBorders?: boolean;
    cellBorders?: boolean;
    stripeRows?: boolean;
}

/** Convert the state saved by the old positions grid configurator (or a persisted GridModel) into a GridSpec. */
export function migratePositionsState(old: LegacyPositionsState, defaults: GridSpec = positionsDefaultSpec): GridSpec {
    if (!old) return null;

    const byField = new Map<string, GridSpecColumn>(defaults.columns.map(c => [(c as GridSpecColumn).field, {...(c as GridSpecColumn)}])),
        known = (f: string) => byField.has(f);

    // Column order, visibility and manual widths
    const order = (old.columns ?? []).map(s => s.colId).filter(known),
        columns = [...order, ...[...byField.keys()].filter(f => !order.includes(f))].map(f => byField.get(f));

    (old.columns ?? []).forEach(s => {
        const col = byField.get(s.colId);
        if (!col) return;
        if (s.hidden) col.hidden = true;
        if (s.manuallySized && s.width) col.width = s.width;
        if (s.pinned) col.pinned = s.pinned;
    });

    // Formats
    Object.entries(old.columnFormats ?? {}).forEach(([field, f]) => {
        const col = byField.get(field);
        if (!col || !f) return;
        if (f.headerName) col.headerName = f.headerName;
        if (f.align) col.align = f.align;
        if (f.flex) {
            col.flex = true;
            delete col.width;
        }
        const format = legacyFormat(f);
        if (format) col.format = format;
    });

    const sortBy = castArray(old.sortBy ?? [])
        .map(s => GridSorter.parse(s))
        .filter(s => s && known(s.colId))
        .map(({colId, sort, abs}) => (abs ? {colId, sort, abs: true} : {colId, sort}));

    return {
        ...defaults,
        columns,
        sortBy,
        groupBy: old.groupBy ?? defaults.groupBy,
        showLeafRows: old.showLeafRows ?? defaults.showLeafRows,
        totals: old.totals === true ? 'bottom' : (old.totals ?? false),
        filters: (old.filters ?? []).filter(f => f.field && !isEmpty(String(f.value ?? ''))),
        rowBorders: !!old.rowBorders,
        cellBorders: !!old.cellBorders,
        stripeRows: !!old.stripeRows
    };
}

function legacyFormat(f: LegacyColumnFormat): FormatSpec {
    switch (f.type) {
        case 'number': {
            const ret: FormatSpec = {type: 'number', numberStyle: f.numberStyle ?? 'plain'};
            if (f.precision != null) ret.precision = f.precision;
            if (f.currency) ret.currency = true;
            if (f.colorSpec) ret.colorSpec = true;
            if (f.ledger) ret.ledger = true;
            return ret;
        }
        case 'date':
            return {type: 'date', dateStyle: f.dateStyle ?? 'date'};
        default:
            return null;
    }
}
